import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { 
  getMLInsights, 
  getAnomalySummary, 
  getPredictionSummary, 
  getMLHealth, 
  getMLAlgorithms,
  predictCpuUsage,
  predictMemoryUsage,
  detectAnomalies,
  getDevicesForML
} from "../api/axios";

// ML Insights Hook
export function useMLInsights(mode: string = 'local') {
  return useQuery({
    queryKey: ["/api/ml/insights", mode],
    queryFn: () => getMLInsights(mode),
    refetchInterval: 30000, // Refetch every 30 seconds
    staleTime: 10000, // Consider data stale after 10 seconds
  });
}

// Anomaly Summary Hook
export function useAnomalySummary(limit: number = 10, mode: string = 'local', sensitivity?: number) {
  return useQuery({
    queryKey: ["/api/ml/anomaly-summary", limit, mode, sensitivity],
    queryFn: () => getAnomalySummary(limit, mode, sensitivity),
    refetchInterval: 30000,
    staleTime: 10000,
  });
}

// Prediction Summary Hook
export function usePredictionSummary(limit: number = 10, mode: string = 'local', hoursAhead?: number) {
  return useQuery({
    queryKey: ["/api/ml/prediction-summary", limit, mode, hoursAhead],
    queryFn: () => getPredictionSummary(limit, mode, hoursAhead),
    refetchInterval: 30000,
    staleTime: 10000,
  });
}

// ML Health Hook
export function useMLHealth() {
  return useQuery({
    queryKey: ["/api/ml/health"],
    queryFn: getMLHealth,
    refetchInterval: 60000, // Refetch every minute
    staleTime: 30000,
  });
}

// ML Algorithms Hook
export function useMLAlgorithms() {
  return useQuery({
    queryKey: ["/api/ml/algorithms"],
    queryFn: getMLAlgorithms,
    staleTime: 300000, // Cache for 5 minutes
  });
}

// Devices for ML Hook
export function useDevicesForML() {
  return useQuery({
    queryKey: ["/api/devices"],
    queryFn: getDevicesForML,
    staleTime: 60000, // Cache for 1 minute
  });
}

// CPU Prediction Hook
export function useCpuPrediction(deviceId: string | number, hoursAhead: number = 24) {
  return useQuery({
    queryKey: ["/api/ml/predictCpuUsage", deviceId, hoursAhead],
    queryFn: () => predictCpuUsage(deviceId, hoursAhead),
    enabled: !!deviceId,
    staleTime: 60000,
  });
}

// Memory Prediction Hook
export function useMemoryPrediction(deviceId: string | number, hoursAhead: number = 24) {
  return useQuery({
    queryKey: ["/api/ml/predictMemoryUsage", deviceId, hoursAhead],
    queryFn: () => predictMemoryUsage(deviceId, hoursAhead),
    enabled: !!deviceId,
    staleTime: 60000,
  });
}

// Anomaly Detection Hook
export function useAnomalyDetection(deviceId: string | number, sensitivity: number = 0.1) {
  return useQuery({
    queryKey: ["/api/ml/detectAnomalies", deviceId, sensitivity],
    queryFn: () => detectAnomalies(deviceId, sensitivity),
    enabled: !!deviceId,
    staleTime: 60000,
  });
}

// Refresh ML Data Hook
export function useRefreshMLData() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async () => {
      // Invalidate all ML-related queries
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["/api/ml/insights"] }),
        queryClient.invalidateQueries({ queryKey: ["/api/ml/anomaly-summary"] }),
        queryClient.invalidateQueries({ queryKey: ["/api/ml/prediction-summary"] }),
        queryClient.invalidateQueries({ queryKey: ["/api/ml/health"] }),
      ]);
    },
  });
}

// Individual Device ML Analysis Hook
export function useDeviceMLAnalysis(deviceId: string | number) {
  const cpuPrediction = useCpuPrediction(deviceId);
  const memoryPrediction = useMemoryPrediction(deviceId);
  const anomalyDetection = useAnomalyDetection(deviceId);
  
  return {
    cpuPrediction,
    memoryPrediction,
    anomalyDetection,
    isLoading: cpuPrediction.isLoading || memoryPrediction.isLoading || anomalyDetection.isLoading,
    error: cpuPrediction.error || memoryPrediction.error || anomalyDetection.error,
  };
}
